import { useDispatch, useStore } from 'react-redux';
import { PrescriptionJob } from '../PrescriptionJob';
import { LayerValues, LayerValuesForFields, VRMForFields } from './SeedingJobCommonTypes';
import {
  setBulkPrescriptionJob,
  setBulkPrescriptionJobs,
  setCurrentJob,
} from '../../../reducers/PrescriptionReducer';
import Field from '../../../model/Field';
import { jobNameWithFieldName } from '../../BulkPrescriptionView/BulkUtils';
import { useLangFile } from '../../../context/LanguageContext';
import { getFieldName } from '../../../helpers/StateInterpreters';
import { calculateIntervalAreas, calculateOverridesArea } from '../PrescriptionUtils';
import { AnyPrescriptionJobStrategy } from '../PrescriptionJobStrategy';
import { useSoilAnalysisForSeedingCalculations } from './useSoilAnalysisForSeedingCalculations';
import { isNullInterval, NULL_INTERVAL_VALUE, restoreNullValues } from '../nullValueRegions';
import { PrescriptionState } from '../PrescriptionState';
import { useCallback, useMemo } from 'react';
import { usePrescriptionJobStrategy } from '../PrescriptionJobStrategyContext';
import { getPrescriptionStrategyFromRef } from '../../../model/prescription/PrescriptionModelsAndVersions';

type BulkPrescriptionJobs = {
  [fieldId: number]: PrescriptionJob;
};

const withoutNullZones = (job: PrescriptionJob): PrescriptionJob => {
  const hasNullInterval = job.intervals?.some(isNullInterval);
  const hasNullValues = job.values?.some((row) => row.includes(NULL_INTERVAL_VALUE));
  if (!hasNullInterval && !hasNullValues) return job;

  return {
    ...job,
    intervals: hasNullInterval
      ? job.intervals.filter((interval) => !isNullInterval(interval))
      : job.intervals,
    values: hasNullValues ? restoreNullValues(job.values) : job.values,
  };
};

/**
 * Coordinates the data fetching, variable-rate-map calculation, and Redux updates required to
 * initialize seeding prescription jobs.
 *
 * The current prescription job strategy owns the prescription-specific calculations. This hook
 * owns the surrounding workflow and selects single-job, initial bulk, or individual bulk-field
 * initialization from the current prescription state.
 *
 * @returns Operations for initializing new jobs, restarting bulk jobs, and recreating archived jobs.
 */
export const usePrescriptionJobOrchestration = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const LangFile = useLangFile();
  const bulkPrescriptionJobs: BulkPrescriptionJobs =
    store.getState().prescription.bulkPrescription.currentJobs;
  const firstField: Field = store.getState().field.selectedField;
  const bulkFields: Field[] = store.getState().prescription.bulkPrescription.fields;
  const { fetchLayerValuesForFields } = useSoilAnalysisForSeedingCalculations();
  const prescriptionJobStrategy = usePrescriptionJobStrategy();

  const requirePrescriptionJobStrategy = useCallback((): AnyPrescriptionJobStrategy => {
    if (!prescriptionJobStrategy) {
      throw new Error('Cannot process a prescription job without a strategy');
    }
    return prescriptionJobStrategy;
  }, [prescriptionJobStrategy]);

  const findFieldById = (id: string) => bulkFields.find((field) => field.fieldId.toString() === id);
  const getJobName = (fieldId: number | string) =>
    jobNameWithFieldName(
      store.getState().prescription.bulkPrescription.prefix,
      getFieldName(findFieldById(fieldId), LangFile)
    );

  /**
   * Recreates the calculated data that is not persisted with an archived seeding prescription job.
   * The rebuilt job is populated with layer values, a variable-rate map, override areas, and
   * interval areas before it is stored as the current job.
   *
   * @param prescriptionJob Archived job containing the persisted prescription settings.
   * @returns The fully reconstructed prescription job.
   */
  const recreatePrescriptionMapForExistingJob = async (prescriptionJob: PrescriptionJob) => {
    if (!prescriptionJob.prescriptionModel) {
      throw new Error('Cannot recreate an archived prescription job without a model');
    }
    const archivedJobStrategy = getPrescriptionStrategyFromRef(prescriptionJob.prescriptionModel);
    const layerValuesForFields: LayerValuesForFields = await fetchLayerValuesForFields(
      archivedJobStrategy.getLayersToFetchValuesFor(),
      prescriptionJob.fieldId
    );
    const vrms = createVariableRateMaps(archivedJobStrategy, layerValuesForFields, prescriptionJob);

    const fieldId: number = prescriptionJob.fieldId;
    const seedingRateMap = vrms[fieldId];
    let singlePrescriptionJob: PrescriptionJob = { ...prescriptionJob };
    singlePrescriptionJob.values = seedingRateMap;
    singlePrescriptionJob.layerValues = layerValuesForFields[fieldId];
    singlePrescriptionJob.overrideAreas = calculateOverridesArea(singlePrescriptionJob);
    singlePrescriptionJob = calculateIntervalAreas(singlePrescriptionJob);

    dispatch(setCurrentJob(singlePrescriptionJob));

    return singlePrescriptionJob;
  };

  /**
   * Creates one variable-rate map per field using the average prescription for the overview job.
   * Returns a list of one variable rate map for single prescription jobs (as opposed to
   * bulk prescriptions).
   *
   * @param prescriptionJobStrategy Strategy responsible for calculating each field's map.
   * @param layerValuesForFields Layer values grouped by field ID.
   * @param prescriptionJob Job whose prescription amount and field size determine the average rate.
   * @returns Variable-rate maps grouped by field ID.
   */
  const createVariableRateMaps = (
    prescriptionJobStrategy: AnyPrescriptionJobStrategy,
    layerValuesForFields: LayerValuesForFields,
    prescriptionJob: PrescriptionJob
  ): VRMForFields => {
    let calculatedSeedingRateMaps: VRMForFields = {};
    const prescription = prescriptionJob.maxPrescription / prescriptionJob.fieldSize;

    Object.keys(layerValuesForFields).forEach((fieldId): void => {
      const layerValues: LayerValues = layerValuesForFields[fieldId];

      const seedingRateMap = prescriptionJobStrategy.createVRM(layerValues, prescription);
      calculatedSeedingRateMaps = { ...calculatedSeedingRateMaps, [fieldId]: seedingRateMap };
    });

    return calculatedSeedingRateMaps;
  };

  /**
   * Fetches the required layer values and initializes the current single or bulk prescription.
   * In bulk mode, the layer-value hook fetches values for every selected bulk field.
   *
   * @param prescriptionStrategy Strategy used to fetch the relevant layers and initialize jobs.
   */
  const fetchAndInitialize = async (prescriptionStrategy: AnyPrescriptionJobStrategy) => {
    const layerValuesForFields: LayerValuesForFields = await fetchLayerValuesForFields(
      prescriptionStrategy.getLayersToFetchValuesFor(),
      store.getState().prescription.currentJob.fieldId
    );
    const vrms = createVariableRateMaps(
      prescriptionStrategy,
      layerValuesForFields,
      store.getState().prescription.currentJob
    );
    initializePrescriptionJob(prescriptionStrategy, layerValuesForFields, vrms);
  };

  const withSourceSurvey = (job: PrescriptionJob): PrescriptionJob => {
    // Match the per-layer survey selection used by getMultipleSurveyLayerValues.
    // The job may still carry the survey ID from its original NDVI/nutrient job.
    const survey = store
      .getState()
      .survey.surveys.find(
        (survey) =>
          survey.fieldId === job.fieldId && Object.keys(survey.newImages).includes(job.layer)
      );
    return {
      ...job,
      surveyImageId: survey && survey.surveyImageId != null ? survey.surveyImageId : null,
    };
  };

  /** Recalculates one field after the bulk intervals have already been applied. */
  const reinitializeBulkField = async (prescriptionJobStrategy: AnyPrescriptionJobStrategy) => {
    const job: PrescriptionJob = store.getState().prescription.currentJob;
    const layers = prescriptionJobStrategy.getLayersToFetchValuesFor();
    let layerValues = job.layerValues;
    if (!layerValues || layers.some((layer) => !layerValues[layer]?.length)) {
      const valuesByField = await fetchLayerValuesForFields(layers, job.fieldId, true);
      layerValues = valuesByField[job.fieldId];
    }

    const prescription = job.maxPrescription / job.fieldSize;
    const vrm = prescriptionJobStrategy.createVRM(layerValues, prescription);
    const updatedJob = withSourceSurvey(
      prescriptionJobStrategy.initializeSingleJob(
        { ...job, totalPrescription: job.maxPrescription },
        vrm,
        layerValues
      )
    );

    dispatch(setCurrentJob(updatedJob));
    dispatch(setBulkPrescriptionJob(job.fieldId, updatedJob));
  };

  const prescriptionState = useMemo(() => {
    const { isBulkPrescription, bulkPrescription } = store.getState().prescription;

    if (!isBulkPrescription) {
      return PrescriptionState.SINGLE_FIELD_PRESCRIPTION;
    }

    if (!prescriptionJobStrategy) {
      if (bulkPrescription.appliedInitialIntervals) {
        return PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW;
      } else {
        return PrescriptionState.BULK_PRESCRIPTION_OVERVIEW;
      }
    }

    if (!prescriptionJobStrategy.hasBulkPrescriptionOverview) {
      return PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW;
    }

    if (!bulkPrescription.appliedInitialIntervals) {
      return PrescriptionState.BULK_PRESCRIPTION_OVERVIEW;
    }

    return PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW;
  }, [prescriptionJobStrategy, store.getState().prescription]);

  const initializePrescription = useCallback(
    (providedStrategy?: AnyPrescriptionJobStrategy) => {
      const strategy = providedStrategy || requirePrescriptionJobStrategy();
      if (prescriptionState === PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW) {
        return reinitializeBulkField(strategy);
      }
      return fetchAndInitialize(strategy);
    },
    [
      requirePrescriptionJobStrategy,
      store,
      reinitializeBulkField,
      fetchAndInitialize,
      prescriptionState,
    ]
  );

  /**
   * Applies calculated maps and layer values to the appropriate prescription-job state.
   * Bulk mode initializes either an overview and its field jobs or field jobs directly.
   */
  const initializePrescriptionJob = (
    prescriptionJobStrategy: AnyPrescriptionJobStrategy,
    layerValuesForFields: LayerValuesForFields,
    vrms: VRMForFields,
    prescriptionJob: PrescriptionJob = store.getState().prescription.currentJob,
    currentBulkJobs: BulkPrescriptionJobs = store.getState().prescription.bulkPrescription
      .currentJobs
  ) => {
    if (store.getState().prescription.isBulkPrescription) {
      let bulkJobs: BulkPrescriptionJobs;
      if (prescriptionJobStrategy.hasBulkPrescriptionOverview) {
        const { currentJob, intervals } = prescriptionJobStrategy.initializeBulkOverview(
          prescriptionJob,
          vrms,
          firstField
        );
        dispatch(
          setCurrentJob({
            ...withSourceSurvey(currentJob),
            jobName: store.getState().prescription.bulkPrescription.prefix,
          })
        );
        bulkJobs = prescriptionJobStrategy.initializeBulkJobs(
          getJobName,
          prescriptionJob,
          currentBulkJobs,
          vrms,
          layerValuesForFields,
          intervals
        );
      } else {
        bulkJobs = prescriptionJobStrategy.initializeBulkJobsWithoutOverview(
          getJobName,
          prescriptionJob,
          currentBulkJobs,
          vrms,
          layerValuesForFields
        );
      }

      const jobsWithSourceSurveys: BulkPrescriptionJobs = {};
      Object.values(bulkJobs).forEach((job) => {
        jobsWithSourceSurveys[job.fieldId] = withSourceSurvey(job);
      });
      dispatch(setBulkPrescriptionJobs(jobsWithSourceSurveys));
      if (!prescriptionJobStrategy.hasBulkPrescriptionOverview) {
        dispatch(setCurrentJob(jobsWithSourceSurveys[firstField.fieldId]));
      }
    } else {
      const newJob: PrescriptionJob = prescriptionJobStrategy.initializeSingleJob(
        prescriptionJob,
        vrms[firstField.fieldId],
        layerValuesForFields[firstField.fieldId]
      );

      dispatch(setCurrentJob(withSourceSurvey(newJob)));
    }
  };

  /**
   * Resets every bulk field to the selected job's settings using the soil layers already held by
   * its field jobs. The variable-rate maps must be recalculated for the shared seeding rate.
   *
   */
  const restartBulkPrescription = useCallback(() => {
    const strategy = requirePrescriptionJobStrategy();
    const layerValuesForFields = {};
    const cleanedBulkJobs: BulkPrescriptionJobs = {};
    const prescriptionJob = withoutNullZones(store.getState().prescription.currentJob);

    Object.values(bulkPrescriptionJobs).forEach((bulkJob) => {
      const cleanedJob = withoutNullZones(bulkJob);
      cleanedBulkJobs[cleanedJob.fieldId] = cleanedJob;
      layerValuesForFields[cleanedJob.fieldId] = cleanedJob.layerValues;
    });
    const vrms = createVariableRateMaps(strategy, layerValuesForFields, prescriptionJob);

    initializePrescriptionJob(
      strategy,
      layerValuesForFields,
      vrms,
      prescriptionJob,
      cleanedBulkJobs
    );
  }, [
    requirePrescriptionJobStrategy,
    store.getState().prescription.currentJob,
    bulkPrescriptionJobs,
    initializePrescriptionJob,
  ]);

  return {
    initializePrescription,
    restartBulkPrescription,
    recreatePrescriptionMapForExistingJob,
    prescriptionState,
  };
};
